import { Controller } from "@hotwired/stimulus";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

export default class extends Controller {
  static targets = ["menu"];

  connect() {
    // Add context menu event listener
    this.element.addEventListener("contextmenu", this.showMenu.bind(this));
    // Add touch press & hold listeners for mobile with passive option
    this.element.addEventListener("touchstart", this.handleTouchStart.bind(this), { passive: true });
    this.element.addEventListener("touchend", this.handleTouchEnd.bind(this), { passive: true });
    this.element.addEventListener("touchmove", this.handleTouchMove.bind(this), { passive: true });
    this.element.addEventListener("touchcancel", this.handleTouchCancel.bind(this), { passive: true });
    // Add click outside listener to close menu
    document.addEventListener("click", this.closeMenu.bind(this));
    // Add escape key listener
    document.addEventListener("keydown", this.handleKeydown.bind(this));
    // Add menu-item-clicked event listener
    this.menuTarget.addEventListener("menu-item-clicked", this.handleMenuItemClick.bind(this));
    // Track scroll lock state
    this.isScrollLocked = false;
    // Touch press & hold state
    this.touchTimer = null;
    this.touchStartPos = null;
    this.longPressDelay = 500; // 500ms for long press
    this.maxTouchMovement = 10; // Max pixels allowed for movement during long press
  }

  disconnect() {
    // Make sure to unlock scroll when controller disconnects
    if (this.isScrollLocked) {
      this.unlockScroll();
    }
    // Clear any pending touch timer
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
    }
    document.removeEventListener("click", this.closeMenu.bind(this));
    document.removeEventListener("keydown", this.handleKeydown.bind(this));
    this.menuTarget.removeEventListener("menu-item-clicked", this.handleMenuItemClick.bind(this));
  }

  handleTouchStart(event) {
    // Only handle single finger touches
    if (event.touches.length !== 1) {
      this.cancelTouchTimer();
      return;
    }

    // Store the initial touch position
    const touch = event.touches[0];
    this.touchStartPos = {
      x: touch.clientX,
      y: touch.clientY,
    };

    // Start the long press timer
    this.touchTimer = setTimeout(() => {
      // Show our custom context menu
      this.showMenuAtPosition(touch.clientX, touch.clientY);
      this.touchTimer = null;
    }, this.longPressDelay);
  }

  handleTouchMove(event) {
    // If we don't have a timer running, ignore
    if (!this.touchTimer || !this.touchStartPos) {
      return;
    }

    // Check if the finger has moved too far
    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.touchStartPos.x);
    const deltaY = Math.abs(touch.clientY - this.touchStartPos.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Cancel long press if moved too far
    if (distance > this.maxTouchMovement) {
      this.cancelTouchTimer();
    }
  }

  handleTouchEnd(event) {
    this.cancelTouchTimer();
  }

  handleTouchCancel(event) {
    this.cancelTouchTimer();
  }

  cancelTouchTimer() {
    if (this.touchTimer) {
      clearTimeout(this.touchTimer);
      this.touchTimer = null;
    }
    this.touchStartPos = null;
  }

  showMenu(event) {
    event.preventDefault();
    this.showMenuAtPosition(event.clientX, event.clientY);
  }

  async showMenuAtPosition(clientX, clientY) {
    // Close any other open context menus first
    const openContextMenus = document.querySelectorAll('dialog[data-context-menu-target="menu"][open]');
    openContextMenus.forEach((menu) => {
      if (menu !== this.menuTarget) {
        const controller = this.application.getControllerForElementAndIdentifier(
          menu.closest('[data-controller="context-menu"]'),
          "context-menu"
        );
        if (controller) {
          controller.closeMenu({ target: document.body });
        }
      }
    });

    // Close all open dropdown popovers
    document.querySelectorAll('[data-controller="dropdown-popover"] dialog[open]').forEach((dialog) => {
      const dropdown = dialog.closest('[data-controller="dropdown-popover"]');
      const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
      if (controller) {
        controller.close();
      }
    });

    // Lock scroll when showing menu
    this.lockScroll();

    // Create a virtual element at the cursor/touch position for Floating UI
    const virtualElement = {
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: clientX,
          y: clientY,
          top: clientY,
          left: clientX,
          right: clientX,
          bottom: clientY,
        };
      },
    };

    // Show the menu first (but positioned off-screen) so we can measure it
    this.menuTarget.style.left = "-9999px";
    this.menuTarget.style.top = "-9999px";
    this.menuTarget.show();

    // Use Floating UI to compute the optimal position
    const { x, y } = await computePosition(virtualElement, this.menuTarget, {
      placement: "bottom-start",
      middleware: [
        offset(4), // Small offset from cursor
        flip({
          fallbackPlacements: ["top-start", "bottom-end", "top-end"],
        }),
        shift({ padding: 8 }), // Keep 8px padding from window edges
      ],
    });

    // Apply the computed position
    this.menuTarget.style.left = `${x}px`;
    this.menuTarget.style.top = `${y}px`;

    // Add animation classes after a frame
    requestAnimationFrame(() => {
      this.menuTarget.classList.add("[&[open]]:scale-100", "[&[open]]:opacity-100");
      // Focus the menu element itself instead of any items
      this.menuTarget.focus();
    });
  }

  closeMenu(event) {
    if (!this.menuTarget.contains(event.target)) {
      this.#closeContextMenu();
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.menuTarget.open) {
      this.#closeContextMenu();
    }
  }

  handleMenuItemClick(event) {
    this.#closeContextMenu();
  }

  // Public method to force close the menu (can be called via data-action)
  close() {
    this.#closeContextMenu();
  }

  // Private method to handle all context menu closing logic
  #closeContextMenu() {
    this.unlockScroll();

    // Reset focus states in the menu
    const menuController = this.application.getControllerForElementAndIdentifier(this.menuTarget, "menu");
    if (menuController) {
      menuController.reset();
    }

    // Close all nested dropdowns immediately (no animation) and remove state attributes
    const nestedDropdowns = this.menuTarget.querySelectorAll('[data-controller="dropdown-popover"]');
    nestedDropdowns.forEach((dropdown) => {
      const controller = this.application.getControllerForElementAndIdentifier(dropdown, "dropdown-popover");
      if (controller && controller.menuTarget.open) {
        controller.buttonTarget.removeAttribute("data-state");
        // Use closeImmediate for faster closing of nested menus
        if (typeof controller.closeImmediate === "function") {
          controller.closeImmediate();
        } else {
          controller.close();
        }
      }
    });

    // Then close the main context menu with animation
    this.menuTarget.classList.remove("[&[open]]:scale-100", "[&[open]]:opacity-100");
    setTimeout(() => {
      this.menuTarget.close();
    }, 100);
  }

  // Add these new methods for scroll locking
  lockScroll() {
    if (this.isScrollLocked) return;

    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Store current body styles
    this.previousBodyPosition = document.body.style.position;
    this.previousBodyTop = document.body.style.top;
    this.previousBodyWidth = document.body.style.width;
    this.previousBodyPointerEvents = document.body.style.pointerEvents;

    // Lock scroll by fixing the body position
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.style.width = "100%";

    // Disable pointer events on the body (but not the menu)
    document.body.style.pointerEvents = "none";
    this.menuTarget.style.pointerEvents = "auto";

    this.isScrollLocked = true;
  }

  unlockScroll() {
    if (!this.isScrollLocked) return;

    // Restore previous body styles
    document.body.style.position = this.previousBodyPosition;
    document.body.style.top = this.previousBodyTop;
    document.body.style.width = this.previousBodyWidth;
    document.body.style.pointerEvents = this.previousBodyPointerEvents;

    // Restore scroll position
    window.scrollTo(0, this.scrollPosition);

    this.isScrollLocked = false;
  }
}
